import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import firebaseFunctionsService from '../../services/firebaseFunctionsService';
import {
  MessageCircle,
  CalendarDays,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Star,
  ArrowLeft,
  DollarSign,
  MessageSquare,
} from 'lucide-react';
import ReviewModal from '../../components/reviews/ReviewModal';
import { hasExistingReview } from '../../services/reviewsService';
import { useAppDispatch } from '../../store/hooks';
import { openOrCreateConversationByUserId } from '../../store/chatThunks';
import airportImage from '../../assets/airportt.png';

const PAGE_SIZE = 12;

const getStatusIcon = status => {
  switch (status) {
    case 'pending':
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'accepted':
      return <CheckCircle className="h-4 w-4 text-blue-500" />;
    case 'rejected':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'cancelled':
      return <XCircle className="h-4 w-4 text-gray-500" />;
    case 'confirm_pending':
      return <Clock className="h-4 w-4 text-orange-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
};

const getStatusColor = status => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300';
    case 'accepted':
      return 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300';
    case 'rejected':
      return 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300';
    case 'completed':
      return 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300';
    case 'cancelled':
      return 'bg-gray-50 border-gray-200 text-gray-800 dark:bg-gray-900/20 dark:border-gray-800 dark:text-gray-300';
    case 'confirm_pending':
      return 'bg-orange-50 border-orange-200 text-orange-800 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-300';
    default:
      return 'bg-gray-50 border-gray-200 text-gray-800 dark:bg-gray-900/20 dark:border-gray-800 dark:text-gray-300';
  }
};

const getStatusLabel = (status, t) => {
  switch (status) {
    case 'pending':
      return t('flexyRideApplications.statusPending');
    case 'accepted':
      return t('flexyRideApplications.statusAccepted');
    case 'rejected':
      return t('flexyRideApplications.statusRejected');
    case 'completed':
      return t('flexyRideApplications.statusCompleted');
    case 'cancelled':
      return t('flexyRideApplications.statusCancelled');
    case 'confirm_pending':
      return t('flexyRidePassengerRequests.paymentPending');
    default:
      return status?.toUpperCase() || t('flexyRideApplications.statusUnknown');
  }
};

export default function PassengerListingRequests() {
  const navigate = useNavigate();
  const { listingId } = useParams();
  const { user, initializing } = usePublicAuth();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [applications, setApplications] = useState([]);
  const [listings, setListings] = useState({});
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState(null);
  const [tipAmount, setTipAmount] = useState('');
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [reviewModal, setReviewModal] = useState({
    visible: false,
    reviewedUserId: null,
    reviewedUserName: '',
    requestId: null,
    role: 'driver',
  });
  const [reviewStatuses, setReviewStatuses] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isMessaging, setIsMessaging] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    // Show applications for user's listings (listingOwnerId == currentUser.uid)
    // Only passenger listings
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'ride_requests'),
        where('listingOwnerId', '==', user.uid),
      ),
      async snapshot => {
        const requests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Fetch listing details for each request
        const applicationsWithListings = await Promise.all(
          requests.map(async request => {
            try {
              const listingDoc = await getDoc(
                doc(db, 'ride_listings', request.listingId),
              );

              return {
                ...request,
                listing: listingDoc.exists()
                  ? { id: listingDoc.id, ...listingDoc.data() }
                  : null,
              };
            } catch (error) {
              console.error('Error fetching listing:', error);
              return { ...request, listing: null };
            }
          }),
        );

        // Filter by passenger type and optional listingId
        const filteredApplications = applicationsWithListings.filter(app => {
          const type = app.listing?.type || app.listingType;
          const matchesType = type === 'passenger';
          const matchesListing = listingId ? app.listingId === listingId : true;
          return matchesType && matchesListing;
        });

        filteredApplications.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
          const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
          return bTime - aTime;
        });

        setApplications(filteredApplications);

        // Store listings for easy access
        const listingsMap = {};
        filteredApplications.forEach(app => {
          if (app.listing) {
            listingsMap[app.listingId] = app.listing;
          }
        });
        setListings(listingsMap);
        setLoading(false);
      },
      error => {
        console.error('Error fetching applications:', error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [user?.uid, listingId]);

  // Check review statuses
  useEffect(() => {
    const checkReviewStatuses = async () => {
      if (!user?.uid || applications.length === 0) return;

      const statuses = {};
      try {
        await Promise.all(
          applications.map(async application => {
            const reviewedUserId = application.applicantId;
            if (!reviewedUserId) {
              statuses[application.id] = false;
              return;
            }

            const hasReview = await hasExistingReview(
              reviewedUserId,
              application.id,
              user.uid,
            );
            statuses[application.id] = hasReview;
          }),
        );
        setReviewStatuses(statuses);
      } catch (error) {
        console.error('Error checking review statuses:', error);
      }
    };

    checkReviewStatuses();
  }, [applications, user?.uid]);

  const handleAccept = async applicationId => {
    try {
      if (!user?.uid) {
        alert(t('flexyRidePassengerRequests.loginToAccept'));
        return;
      }

      const application = applications.find(app => app.id === applicationId);
      if (!application) {
        alert(t('flexyRidePassengerRequests.applicationNotFound'));
        return;
      }

      // Security check: Verify that current user is the listing owner
      if (application.listingOwnerId !== user.uid) {
        alert(t('flexyRidePassengerRequests.onlyOwnListings'));
        return;
      }

      const targetListingId = application.listingId;
      if (!targetListingId) {
        alert(t('flexyRideDetail.listingIdNotFound'));
        return;
      }

      // Verify listing ownership
      const listingDoc = await getDoc(
        doc(db, 'ride_listings', targetListingId),
      );
      if (!listingDoc.exists()) {
        alert(t('flexyRideDetail.listingNotFound'));
        return;
      }

      const listingData = listingDoc.data();
      if (listingData.createdBy !== user.uid) {
        alert(t('flexyRidePassengerRequests.onlyOwnListings'));
        return;
      }

      // Calculate total price with fee for balance check
      const rawPrice = application?.price || application?.listing?.price || 0;
      const currency =
        application?.currency || application?.listing?.currency || 'CAD';
      const costNum =
        typeof rawPrice === 'string'
          ? parseFloat(String(rawPrice).replace(/[^0-9.]/g, ''))
          : Number(rawPrice);

      if (!isNaN(costNum) && costNum > 0) {
        const fee = costNum * 0.15; // 15% service fee
        const total = costNum + fee;

        // Get current balance from Firestore
        let currentBalance = 0;
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            currentBalance = Number(userData?.walletBalance || 0);
          }
        } catch (error) {
          console.warn('Error fetching balance from Firestore:', error);
        }

        if (currentBalance < total) {
          alert(
            t('flexyRidePassengerRequests.insufficientBalance', {
              currency,
              total: total.toFixed(2),
              balance: currentBalance.toFixed(2),
            }),
          );
          return;
        }
      }

      // Accept the selected application
      await updateDoc(doc(db, 'ride_requests', applicationId), {
        status: 'accepted',
        updatedAt: serverTimestamp(),
      });

      // Reject all other pending requests for the same listing
      const otherPendingRequests = await Promise.all(
        applications
          .filter(
            app =>
              app.listingId === targetListingId &&
              app.status === 'pending' &&
              app.id !== applicationId,
          )
          .map(app => app.id),
      );

      if (otherPendingRequests.length > 0) {
        const batch = writeBatch(db);
        otherPendingRequests.forEach(requestId => {
          batch.update(doc(db, 'ride_requests', requestId), {
            status: 'rejected',
            updatedAt: serverTimestamp(),
          });
        });
        await batch.commit();
      }

      alert(t('flexyRidePassengerRequests.acceptSuccess'));
    } catch (error) {
      console.error('Error accepting application:', error);
      alert(t('flexyRidePassengerRequests.acceptError'));
    }
  };

  const handleReject = async applicationId => {
    try {
      if (!user?.uid) {
        alert(t('flexyRidePassengerRequests.loginToReject'));
        return;
      }

      const application = applications.find(app => app.id === applicationId);
      if (!application) {
        alert(t('flexyRidePassengerRequests.applicationNotFound'));
        return;
      }

      // Security check: Verify that current user is the listing owner
      if (application.listingOwnerId !== user.uid) {
        alert(t('flexyRidePassengerRequests.onlyOwnListings'));
        return;
      }

      await updateDoc(doc(db, 'ride_requests', applicationId), {
        status: 'rejected',
        updatedAt: serverTimestamp(),
      });

      alert(t('flexyRidePassengerRequests.rejectSuccess'));
    } catch (error) {
      console.error('Error rejecting application:', error);
      alert(t('flexyRidePassengerRequests.rejectError'));
    }
  };

  const handleOpenPaymentModal = application => {
    const rawPrice = application?.price || application?.listing?.price || 0;
    const currency =
      application?.currency || application?.listing?.currency || 'CAD';
    const costNum =
      typeof rawPrice === 'string'
        ? parseFloat(String(rawPrice).replace(/[^0-9.]/g, ''))
        : Number(rawPrice);
    const fee = !isNaN(costNum) ? costNum * 0.15 : 0;
    const total = !isNaN(costNum) ? costNum + fee : 0;

    setPaymentModalData({
      requestId: application.id,
      applicantId: application.applicantId,
      currency,
      subtotal: costNum,
      fee,
      total,
      itemTitle:
        application.listing?.from && application.listing?.to
          ? t('flexyRidePassengerRequests.rideRequestFormatted', {
              from: application.listing.from,
              to: application.listing.to,
            })
          : t('flexyRidePassengerRequests.rideRequestTitle'),
    });
    setTipAmount('');
    setShowPaymentModal(true);
  };

  const handleConfirmPayment = async () => {
    if (!paymentModalData || !user?.uid) return;

    if (confirmingPayment) return;

    try {
      setConfirmingPayment(true);

      const requestDoc = await getDoc(
        doc(db, 'ride_requests', paymentModalData.requestId),
      );

      if (!requestDoc.exists()) {
        throw new Error(t('flexyRidePassengerRequests.requestNotFound'));
      }

      const requestData = requestDoc.data();
      if (requestData.status === 'completed') {
        setShowPaymentModal(false);
        setPaymentModalData(null);
        alert(t('flexyRidePassengerRequests.paymentAlreadyConfirmed'));
        return;
      }

      // Confirmer pays total (subtotal + fee + tip); counterparty receives subtotal + tip
      const payerId = user.uid; // listing owner (passenger)
      const receiverId = paymentModalData.applicantId; // driver

      if (!payerId || !receiverId) {
        throw new Error(t('flexyRidePassengerRequests.payerReceiverMissing'));
      }

      const amount = Number(paymentModalData.subtotal || 0) || 0;
      const fee = Number(paymentModalData.fee || 0) || 0;
      const total = Number(paymentModalData.total || 0) || 0;
      const currency = paymentModalData.currency || 'CAD';
      const itemTitle =
        paymentModalData.itemTitle ||
        t('flexyRidePassengerRequests.rideRequestTitle');

      const authToken = await auth.currentUser?.getIdToken();
      if (!authToken) {
        throw new Error(t('flexyRidePassengerRequests.userNotAuthenticated'));
      }

      const tipAmountValue = parseFloat(tipAmount || 0);
      const paymentResult =
        await firebaseFunctionsService.confirmDeliveryPayment(
          paymentModalData.requestId,
          payerId,
          receiverId,
          amount,
          fee,
          total,
          currency,
          itemTitle,
          authToken,
          tipAmountValue,
          'ride_requests',
        );

      if (paymentResult.alreadyCompleted) {
        setShowPaymentModal(false);
        setPaymentModalData(null);
        alert(t('flexyRidePassengerRequests.paymentAlreadyConfirmed'));
        return;
      }

      // Wait briefly for batch commit
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Mark request as completed in Firestore
      await updateDoc(doc(db, 'ride_requests', paymentModalData.requestId), {
        status: 'completed',
        completedAt: serverTimestamp(),
        confirmedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Update local state to completed
      setApplications(prev =>
        prev.map(app =>
          app.id === paymentModalData.requestId
            ? { ...app, status: 'completed' }
            : app,
        ),
      );

      setShowPaymentModal(false);
      setPaymentModalData(null);
      alert(t('flexyRidePassengerRequests.paymentConfirmSuccess'));
    } catch (error) {
      console.error('Error confirming payment:', error);
      alert(
        error.message || t('flexyRidePassengerRequests.paymentConfirmError'),
      );
    } finally {
      setTipAmount('');
      setConfirmingPayment(false);
    }
  };

  const handleMessage = async application => {
    if (isMessaging) return;
    setIsMessaging(true);
    try {
      const applicantId = application.applicantId;
      if (!applicantId) {
        setIsMessaging(false);
        return;
      }

      const applicantDoc = await getDoc(doc(db, 'users', applicantId));
      if (!applicantDoc.exists()) {
        setIsMessaging(false);
        return;
      }

      const applicantData = applicantDoc.data();
      const applicantName =
        applicantData.displayName ||
        `${applicantData.firstName || ''} ${
          applicantData.lastName || ''
        }`.trim() ||
        applicantData.email?.split('@')[0] ||
        t('common.unknownUser');

      const conversation = await dispatch(
        openOrCreateConversationByUserId(applicantId, applicantName),
      );
      if (conversation?.id) {
        navigate(`/chat/${conversation.id}`);
      }
    } catch (e) {
      console.log('Failed to open message:', e?.message);
    } finally {
      setIsMessaging(false);
    }
  };

  const paginatedApplications = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return applications.slice(start, end);
  }, [applications, currentPage]);

  const totalPages = Math.ceil(applications.length / PAGE_SIZE);

  // Derive primary listing info for header card (like TravelerTripRequests)
  const primaryListing = useMemo(() => {
    if (!applications.length) return null;
    const first = applications[0];
    return first.listing || listings[first.listingId] || null;
  }, [applications, listings]);

  if (initializing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />

        {/* Hero-style loading skeleton */}
        <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[400px]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="h-16 bg-white/20 dark:bg-slate-700/50 rounded-xl mb-4 w-3/4 animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-full animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-5/6 animate-pulse"></div>
              </div>
              <div className="relative">
                <div className="w-full h-80 bg-white/20 dark:bg-slate-700/50 rounded-2xl animate-pulse"></div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-6xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg text-center border border-gray-200 dark:border-slate-800">
            <p className="text-gray-600 dark:text-slate-400 mb-4">
              {t('flexyRidePassengerRequests.loginToView')}
            </p>
            <a
              href="/auth/login"
              className="inline-flex rounded-2xl bg-black dark:bg-yellow-500 px-5 py-3 text-sm font-semibold text-yellow-400 dark:text-black transition hover:bg-gray-900 dark:hover:bg-yellow-400"
            >
              {t('common.signIn')}
            </a>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      {/* Hero Section similar to TravelerTripRequests */}
      <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[400px]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-4 drop-shadow-lg">
                {t('flexyRidePassengerRequests.title')}
              </h1>
              <p className="text-xl text-yellow-50 mb-8 max-w-2xl">
                {t('flexyRidePassengerRequests.subtitle')}
              </p>
            </div>
            <div className="relative">
              <img
                src={airportImage}
                alt="Airport"
                className="w-full h-auto object-contain"
                style={{ maxHeight: '350px' }}
              />
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white/20 to-transparent"
          style={{ zIndex: 1 }}
        ></div>
      </section>

      <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 mb-6 text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="font-semibold">{t('common.back')}</span>
        </button>

        {/* Listing Info Card (top summary, similar to SeatListingRequests) */}
        {primaryListing && (
          <div className="rounded-2xl bg-gradient-to-br from-white to-gray-50 dark:from-slate-900 dark:to-slate-800 p-6 shadow-lg border border-gray-200 dark:border-slate-800 mb-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRidePassengerRequests.route')}
                </p>
                <p className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                  <span className="block">
                    {primaryListing.from || t('home.fromFallback')}
                  </span>
                  <span className="text-gray-400 dark:text-slate-500 mx-2 my-1 block">
                    ↓
                  </span>
                  <span className="block">
                    {primaryListing.to || t('home.toFallback')}
                  </span>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <CalendarDays className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-gray-500 dark:text-slate-400 mb-1">
                    {t('travelerTripRequests.departure')}
                  </p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                    {primaryListing.date || t('flexyRideSeat.dateNotSet')}{' '}
                    {primaryListing.time
                      ? t('tripDetail.atTime', { time: primaryListing.time })
                      : ''}
                  </p>
                </div>
              </div>

              {primaryListing.seats && (
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 dark:text-slate-400 mb-1">
                      {t('flexyRidePassengerRequests.seatsRequested')}
                    </p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {primaryListing.seats}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-gray-500 dark:text-slate-400">
              {t('common.loading')}
            </div>
          </div>
        ) : paginatedApplications.length === 0 ? (
          <div className="rounded-2xl bg-white dark:bg-slate-900 p-12 shadow-lg border-2 border-gray-200 dark:border-slate-800 text-center">
            <MessageCircle className="h-16 w-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-slate-400">
              {t('flexyRidePassengerRequests.empty')}
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {paginatedApplications.map(application => {
                const listing =
                  application.listing || listings[application.listingId];
                if (!listing) return null;

                const isPending = application.status === 'pending';
                const isCompleted = application.status === 'completed';
                const isAccepted = application.status === 'accepted';
                const isConfirmPending =
                  application.status === 'confirm_pending';

                return (
                  <div
                    key={application.id}
                    className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-slate-700 hover:shadow-md transition"
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-xs font-bold">
                        {t('flexyRidePassengerRequests.requestingDriver')}
                      </span>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(
                          application.status,
                        )}`}
                      >
                        <div className="flex items-center gap-1.5">
                          {getStatusIcon(application.status)}
                          {getStatusLabel(application.status, t)}
                        </div>
                      </span>
                    </div>

                    {/* Applicant Info */}
                    <div className="flex items-center gap-2 mb-4">
                      <User className="h-4 w-4 text-gray-500 dark:text-slate-400" />
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {application.applicantName || t('common.unknownUser')}
                      </span>
                    </div>

                    {/* Route */}
                    <div className="flex items-start gap-2 mb-4">
                      <MapPin className="h-4 w-4 text-gray-500 dark:text-slate-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1">
                          {listing.from || t('home.fromFallback')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                          →
                        </p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1">
                          {listing.to || t('home.toFallback')}
                        </p>
                      </div>
                    </div>

                    {/* Date & Time */}
                    <div className="flex items-center gap-2 mb-4 text-sm text-gray-600 dark:text-slate-400">
                      <CalendarDays className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                      <span>
                        {listing.date || t('flexyRideSeat.dateNotSet')} •{' '}
                        {listing.time || t('flexyRideSeat.timeNotSet')}
                      </span>
                    </div>

                    {/* Offer Section */}
                    {(application.price ||
                      application.carBrand ||
                      application.note) && (
                      <div className="mb-4 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600">
                        <p className="text-xs font-semibold uppercase text-gray-500 dark:text-slate-400 mb-2">
                          {t('flexyRidePassengerRequests.offer')}
                        </p>
                        {application.price && (
                          <div className="flex items-center gap-2 mb-2">
                            <DollarSign className="h-3 w-3 text-gray-500 dark:text-slate-400" />
                            <span className="text-sm text-gray-900 dark:text-white">
                              {t('flexyRidePassengerRequests.offerPriceValue', {
                                price: application.price,
                                currency:
                                  application.currency ||
                                  listing.currency ||
                                  'CAD',
                              })}
                            </span>
                          </div>
                        )}
                        {application.carBrand && (
                          <div className="mb-2">
                            <span className="text-sm text-gray-900 dark:text-white">
                              {t('flexyRidePassengerRequests.offerCarValue', {
                                brand: application.carBrand,
                              })}
                            </span>
                          </div>
                        )}
                        {application.note && (
                          <div className="p-2 bg-white dark:bg-slate-800 rounded border border-gray-200 dark:border-slate-600">
                            <p className="text-xs text-gray-700 dark:text-slate-300 line-clamp-2">
                              {application.note}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Completed Status */}
                    {isCompleted && (
                      <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                        <p className="text-sm font-semibold text-green-900 dark:text-green-200 mb-2">
                          {t('flexyRidePassengerRequests.paymentCompleted')}
                        </p>
                        <p className="text-xs text-green-700 dark:text-green-300 mb-3">
                          {t('flexyRideMyRequests.completedMessage')}
                        </p>
                        {reviewStatuses[application.id] ? (
                          <div className="px-3 py-1 bg-gray-100 dark:bg-slate-700 rounded-lg inline-block">
                            <span className="text-xs font-semibold text-gray-600 dark:text-slate-400">
                              {t('flexyRideApplications.reviewed')}
                            </span>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              if (!reviewStatuses[application.id]) {
                                const reviewedUserId = application.applicantId;
                                const reviewedUserName =
                                  application.applicantName ||
                                  t('common.unknownUser');
                                setReviewModal({
                                  visible: true,
                                  reviewedUserId,
                                  reviewedUserName,
                                  requestId: application.id,
                                  role: 'driver',
                                });
                              }
                            }}
                            disabled={reviewStatuses[application.id]}
                            className="flex items-center gap-2 px-3 py-1.5 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Star className="h-3 w-3 text-yellow-600 dark:text-yellow-400" />
                            <span className="text-xs font-semibold text-yellow-800 dark:text-yellow-300">
                              {t('flexyRideApplications.rate')}
                            </span>
                          </button>
                        )}
                      </div>
                    )}

                    {/* Payment Pending Status */}
                    {(isConfirmPending || isAccepted) && (
                      <div className="mb-4 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                        <p className="text-sm font-semibold text-orange-900 dark:text-orange-200 mb-2">
                          {t('flexyRidePassengerRequests.paymentPending')}
                        </p>
                        <p className="text-xs text-orange-700 dark:text-orange-300 mb-3">
                          {t('flexyRideMyRequests.acceptedMessage')}
                        </p>
                        <button
                          onClick={() => handleOpenPaymentModal(application)}
                          className="w-full px-4 py-2.5 bg-blue-500 text-white rounded-xl text-sm font-semibold hover:bg-blue-600 transition"
                        >
                          {t('flexyRideMyRequests.confirmPaymentButton')}
                        </button>
                      </div>
                    )}

                    {/* Action Buttons */}
                    {isPending && (
                      <div className="flex gap-2 mb-4">
                        <button
                          onClick={() => handleReject(application.id)}
                          className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 transition"
                        >
                          {t('common.reject')}
                        </button>
                        <button
                          onClick={() => handleAccept(application.id)}
                          className="flex-1 px-4 py-2.5 bg-green-500 text-white rounded-xl text-sm font-semibold hover:bg-green-600 transition"
                        >
                          {t('common.accept')}
                        </button>
                      </div>
                    )}

                    {/* Message and Support Buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleMessage(application)}
                        disabled={isMessaging}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-xl text-sm font-semibold hover:bg-gray-200 dark:hover:bg-slate-600 transition disabled:opacity-50"
                      >
                        <MessageCircle className="h-4 w-4" />
                        {isMessaging
                          ? t('chat.opening')
                          : t('myRequests.message')}
                      </button>
                      <button
                        onClick={() => navigate('/profile/support')}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-xl text-sm font-semibold hover:bg-gray-200 dark:hover:bg-slate-600 transition"
                      >
                        <MessageSquare className="h-4 w-4" />
                        {t('profile.supportTitle') || 'Support'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                >
                  {t('common.prev')}
                </button>
                <span className="text-sm text-gray-600 dark:text-slate-400">
                  {t('common.pageOf', {
                    current: currentPage,
                    total: totalPages,
                  })}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage(prev => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                >
                  {t('common.next')}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Payment Modal */}
      {showPaymentModal && paymentModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-200 dark:border-slate-800">
            <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-4 text-center">
              {t('flexyRideMyRequests.modalTitle')}
            </h3>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-slate-700">
                <span className="text-sm text-gray-600 dark:text-slate-400">
                  {t('flexyRideMyRequests.modalSubtotal')}
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {paymentModalData.currency}{' '}
                  {paymentModalData.subtotal.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-slate-700">
                <span className="text-sm text-gray-600 dark:text-slate-400">
                  {t('flexyRideMyRequests.modalFees')}
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {paymentModalData.currency} {paymentModalData.fee.toFixed(2)}
                </span>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  {t('flexyRideMyRequests.modalTipLabel')}
                </label>
                <input
                  type="text"
                  placeholder={t('flexyRideMyRequests.modalTipPlaceholder')}
                  value={tipAmount}
                  onChange={e => {
                    const cleaned = e.target.value.replace(/[^0-9.]/g, '');
                    const parts = cleaned.split('.');
                    const filtered =
                      parts.length > 2
                        ? parts[0] + '.' + parts.slice(1).join('')
                        : cleaned;
                    setTipAmount(filtered);
                  }}
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              {tipAmount && parseFloat(tipAmount) > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-slate-700">
                  <span className="text-sm text-gray-600 dark:text-slate-400">
                    {t('flexyRideMyRequests.modalTip')}
                  </span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {paymentModalData.currency}{' '}
                    {parseFloat(tipAmount || 0).toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center py-2">
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {t('flexyRideMyRequests.modalTotal')}
                </span>
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {paymentModalData.currency}{' '}
                  {(
                    paymentModalData.total + parseFloat(tipAmount || 0)
                  ).toFixed(2)}
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setTipAmount('');
                  setPaymentModalData(null);
                }}
                className="flex-1 px-4 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-slate-700 transition"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmPayment}
                disabled={confirmingPayment}
                className="flex-1 px-4 py-3 bg-black dark:bg-yellow-500 text-yellow-500 dark:text-black rounded-xl font-bold hover:bg-gray-900 dark:hover:bg-yellow-400 transition disabled:opacity-50"
              >
                {confirmingPayment
                  ? t('flexyRideMyRequests.modalProcessing')
                  : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ReviewModal
        visible={reviewModal.visible}
        onClose={() =>
          setReviewModal({
            visible: false,
            reviewedUserId: null,
            reviewedUserName: '',
            requestId: null,
            role: 'driver',
          })
        }
        reviewedUserId={reviewModal.reviewedUserId}
        reviewedUserName={reviewModal.reviewedUserName}
        requestId={reviewModal.requestId}
        userRole={reviewModal.role}
        onReviewSubmitted={() => {
          if (reviewModal.requestId) {
            setReviewStatuses(prev => ({
              ...prev,
              [reviewModal.requestId]: true,
            }));
          }
        }}
      />
    </div>
  );
}
